import type { TabItem } from '@/components/CallsTabs/types';

/**
 * Determines which tab should be selected based on manual selection and available tabs.
 *
 * Selection priority:
 * 1. If user manually selected a tab and it still exists, use it
 * 2. Otherwise, select the newest (last) tab
 *
 * @param tabItems - Available tab items
 * @param manuallySelectedId - User's manual selection (if any)
 * @returns The ID that should be selected, or null if no tabs available
 */
export function determineReviewTabSelection(
  tabItems: TabItem[] | undefined,
  manuallySelectedId: string | null,
): string | null {
  if (!tabItems || tabItems.length === 0) {
    return null;
  }

  // If user manually selected a tab and it still exists, use it
  if (manuallySelectedId) {
    const manualStillExists = tabItems.some(item => item.id === manuallySelectedId);
    if (manualStillExists) {
      return manuallySelectedId;
    }
  }

  // Otherwise, select the newest (last) non-AI-audit tab. AI Audit can be the
  // newest item chronologically but it summarises the call/eligibility work
  // below it — defaulting to the underlying tab keeps the existing UX intact.
  for (let i = tabItems.length - 1; i >= 0; i--) {
    if (tabItems[i].type !== 'ai_audit') {
      return tabItems[i].id || null;
    }
  }
  // All items are AI Audit (edge case): fall back to the last one.
  const mostRecent = tabItems[tabItems.length - 1];
  return mostRecent?.id || null;
}
